import * as React from "react";
import { DraftFunction } from "use-immer";
import { Draft } from "immer";
import { Card, CardHeader, CardTitle } from "../ui/Card";
import { MacroBar } from "../ui/MacroBar";
import { detectActivePreset, DietPresetPicker } from "./DietPresetPicker";
import { KcalInput } from "./KcalInput";
import { MacroRow } from "./MacroRow";
import { BmrCalculatorSheet } from "./BmrCalculatorSheet";
import { NeedDefinitionWithEditedFlags } from "../../data/need";
import {
  applyAbsoluteChange,
  applyPercentChange,
  FixedField,
  getAbsoluteFromPercent,
} from "../../utils/needMath";

export interface NeedCardProps {
  need: NeedDefinitionWithEditedFlags;
  personName?: string;
  updateNeed: (updater: DraftFunction<NeedDefinitionWithEditedFlags>) => void;
}

export const NeedCard = ({ need, personName, updateNeed }: NeedCardProps) => {
  const [fixedField, setFixedField] =
    React.useState<FixedField>("carbsPercent");
  const [bmrOpen, setBmrOpen] = React.useState(false);

  const activePreset = detectActivePreset(
    need.fatPercent,
    need.carbsPercent,
    need.proteinPercent,
  );

  const mutate = (
    mutator: (draft: Draft<NeedDefinitionWithEditedFlags>) => void,
  ) => {
    updateNeed((draft) => {
      mutator(draft);
      draft.edited = true;
    });
  };

  const handleKcal = (kcal: number) => {
    mutate((draft) => {
      draft.kcal = kcal;
      draft.fat = getAbsoluteFromPercent(draft, 9, draft.fatPercent);
      draft.carbs = getAbsoluteFromPercent(draft, 4, draft.carbsPercent);
      draft.protein = getAbsoluteFromPercent(draft, 4, draft.proteinPercent);
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {personName ? `Bedarf für ${personName}` : "Bedarf"}
        </CardTitle>
      </CardHeader>

      <div className="space-y-5">
        <DietPresetPicker
          activeId={activePreset}
          onPick={(preset) => {
            mutate((draft) => {
              draft.fatPercent = preset.fatPercent;
              draft.carbsPercent = preset.carbsPercent;
              draft.proteinPercent = preset.proteinPercent;
              draft.fat = getAbsoluteFromPercent(draft, 9, preset.fatPercent);
              draft.carbs = getAbsoluteFromPercent(
                draft,
                4,
                preset.carbsPercent,
              );
              draft.protein = getAbsoluteFromPercent(
                draft,
                4,
                preset.proteinPercent,
              );
            });
            setFixedField(preset.fixedField);
          }}
        />

        <KcalInput
          value={need.kcal}
          onChange={handleKcal}
          onOpenCalculator={() => setBmrOpen(true)}
        />

        <div>
          <MacroBar
            fat={need.fat}
            carbs={need.carbs}
            protein={need.protein}
            kcal={need.kcal}
            showExtendedInfo={true}
          />
        </div>

        <div className="space-y-4">
          <MacroRow
            tone="fat"
            grams={need.fat}
            percent={need.fatPercent}
            isAuto={fixedField === "fatPercent"}
            onPercentChange={(v) =>
              mutate((d) => applyPercentChange(d, "fatPercent", v, fixedField))
            }
            onGramsChange={(v) =>
              mutate((d) => applyAbsoluteChange(d, "fat", v, fixedField))
            }
            onToggleAuto={() =>
              setFixedField(
                fixedField === "fatPercent" ? "carbsPercent" : "fatPercent",
              )
            }
          />
          <MacroRow
            tone="carbs"
            grams={need.carbs}
            percent={need.carbsPercent}
            isAuto={fixedField === "carbsPercent"}
            onPercentChange={(v) =>
              mutate((d) =>
                applyPercentChange(d, "carbsPercent", v, fixedField),
              )
            }
            onGramsChange={(v) =>
              mutate((d) => applyAbsoluteChange(d, "carbs", v, fixedField))
            }
            onToggleAuto={() =>
              setFixedField(
                fixedField === "carbsPercent"
                  ? "proteinPercent"
                  : "carbsPercent",
              )
            }
          />
          <MacroRow
            tone="protein"
            grams={need.protein}
            percent={need.proteinPercent}
            isAuto={fixedField === "proteinPercent"}
            onPercentChange={(v) =>
              mutate((d) =>
                applyPercentChange(d, "proteinPercent", v, fixedField),
              )
            }
            onGramsChange={(v) =>
              mutate((d) => applyAbsoluteChange(d, "protein", v, fixedField))
            }
            onToggleAuto={() =>
              setFixedField(
                fixedField === "proteinPercent"
                  ? "carbsPercent"
                  : "proteinPercent",
              )
            }
          />
        </div>
      </div>

      <BmrCalculatorSheet
        open={bmrOpen}
        onClose={() => setBmrOpen(false)}
        onSubmit={(bmr) => handleKcal(Math.round(bmr))}
      />
    </Card>
  );
};
